<template>
  <auth-shell
    v-if="activePanel"
    :wide="activePanel === GATE_PANELS.TERMS"
  >
    <component :is="PANEL_COMPONENTS[activePanel]" />
  </auth-shell>
  <slot v-else />
</template>

<script setup>
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'

  import { useAccountStore } from 'src/stores/account'
  import { useOnboardingStore } from 'src/stores/account/onboarding'
  import { useAuthStore } from 'src/stores/auth'

  import { GATE_PANELS } from 'src/services/consts/app.const'
  import {
    hasAccountLoadError,
    isTrialExpiredByDate,
    resolveOnboardingStep,
    resolvePublicRoutePanel,
  } from 'src/services/helpers/auth.helper'

  import AuthErrorPanel from 'src/components/auth/AuthErrorPanel.vue'
  import AuthLoadingPanel from 'src/components/auth/AuthLoadingPanel.vue'
  import AuthShell from 'src/components/auth/AuthShell.vue'
  import EmailVerifiedSessionPanel from 'src/components/auth/EmailVerifiedSessionPanel.vue'
  import FounderWelcomePanel from 'src/components/auth/FounderWelcomePanel.vue'
  import InviteeWelcomePanel from 'src/components/auth/InviteeWelcomePanel.vue'
  import InviteSessionPanel from 'src/components/auth/InviteSessionPanel.vue'
  import OnboardingTermsPanel from 'src/components/auth/OnboardingTermsPanel.vue'

  const route = useRoute()
  const accountStore = useAccountStore()
  const onboardingStore = useOnboardingStore()
  const authStore = useAuthStore()

  // Every panel is self-contained (no props, no emits) so the gate only decides
  // WHICH one renders — adding a step means a resolver id plus a row here.
  const PANEL_COMPONENTS = {
    // Auth0 / first /me in flight, or logout in progress.
    [GATE_PANELS.SESSION_LOADING]: AuthLoadingPanel,
    // Auth0 callback failed, GET /me never landed, leftover elapsed trial, or trial_expired 401.
    [GATE_PANELS.SESSION_ERROR]: AuthErrorPanel,
    // Named account missing — founder names the company and finishes setup.
    [GATE_PANELS.FOUNDER_WELCOME]: FounderWelcomePanel,
    // Named account, onboardingDone false — invitee welcome after join.
    [GATE_PANELS.INVITEE_WELCOME]: InviteeWelcomePanel,
    // Transient overlay after welcome — terms acceptance before backend mutations.
    [GATE_PANELS.TERMS]: OnboardingTermsPanel,
    // Authenticated on /accept-invite — match / mismatch / invalid vs session.
    [GATE_PANELS.INVITE_SESSION]: InviteSessionPanel,
    // Authenticated on /email-verified — result honesty + continue / switch.
    [GATE_PANELS.EMAIL_VERIFIED_SESSION]: EmailVerifiedSessionPanel,
  }

  const WELCOME_PANELS = new Set([GATE_PANELS.FOUNDER_WELCOME, GATE_PANELS.INVITEE_WELCOME])

  /** Only render an id the registry knows — a missing component would render nothing. */
  const isRenderablePanel = (panelId) => Boolean(panelId && PANEL_COMPONENTS[panelId])

  const isPublicRoute = computed(() => !!route.meta?.public)

  // Auth0 or the FIRST GET /me is still in flight. `accountStore.isLoading` is
  // TanStack's isPending && isFetching, so neither a retry after a failure nor a
  // placeholderData hit counts — both leave `isPending` false, so those states fall
  // through to the error panel and the live app respectively, as they always have.
  const isFetchingSession = computed(() => authStore.isLoading || accountStore.isLoading)

  // Auth0 failed, GET /me never landed, or leftover trial date has elapsed.
  const hasSessionError = computed(
    () =>
      !!authStore.error ||
      hasAccountLoadError({
        isAuthenticated: authStore.isAuthenticated,
        accountError: accountStore.error,
        accountData: accountStore.accountData,
      }) ||
      isTrialExpiredByDate(accountStore.accountData ?? {})
  )

  // The one place loading-vs-error is decided, so both branches below agree.
  // Fetching wins: a stale Auth0 callback error must not replace the first-load
  // spinner while /me is still resolving. Null when the session is neither.
  const sessionPanel = computed(() => {
    if (isFetchingSession.value) return GATE_PANELS.SESSION_LOADING
    if (hasSessionError.value) return GATE_PANELS.SESSION_ERROR

    return null
  })

  // The onboarding step still owed, or null when onboarding is complete.
  const onboardingPanel = computed(() => {
    const step = resolveOnboardingStep(accountStore.accountData)
    if (!step) return null

    if (onboardingStore.isTermsStep && WELCOME_PANELS.has(step)) {
      return GATE_PANELS.TERMS
    }

    return isRenderablePanel(step) ? step : GATE_PANELS.SESSION_LOADING
  })

  // meta.public means "do not force a login here"
  const publicRoutePanel = computed(() => {
    if (!authStore.isAuthenticated) return null
    // Only a failing session takes over the route panel. A clean fetch must not,
    // because these panels read authStore.auth0User.email, which is populated
    // before /me resolves — waiting would stall the invite landing for no reason.
    if (hasSessionError.value) return sessionPanel.value

    const routePanel = resolvePublicRoutePanel(route.name)
    if (isRenderablePanel(routePanel)) return routePanel

    return onboardingPanel.value
  })

  // sessionPanel beats the signed-out branch, so a failed callback
  // (email_not_verified never reaches a session) gets the error panel, not a spinner.
  const activePanel = computed(() => {
    if (authStore.isLoggingOut) return GATE_PANELS.SESSION_LOADING
    if (isPublicRoute.value) return publicRoutePanel.value
    if (sessionPanel.value) return sessionPanel.value
    if (!authStore.isAuthenticated) return GATE_PANELS.SESSION_LOADING

    return onboardingPanel.value
  })
</script>
